import type { DropdownOption, SectionConfig } from '../shared';

export const APP_NAME = 'iSecondaryFragments';
export const APP_VERSION = '1.0.0';
export const APP_VERSION_POLICY = { currentVersion: APP_VERSION };
const DJANGO_STATIC_PREVIEW_ROOT = '/static/images/secondary_fragments';

export const CHARGE_SHAPE_OPTIONS = [
  { value: 'Spherical', label: 'Spherical' },
  { value: 'Cylindrical', label: 'Cylindrical' }
] as const satisfies readonly DropdownOption[];

export const FRAGMENT_SHAPE_OPTIONS = [
  { value: 'CIRCULAR CYLINDER (LONG ROD), SIDE-ON', label: 'CIRCULAR CYLINDER (LONG ROD), SIDE-ON' },
  { value: 'SPHERE', label: 'SPHERE' },
  { value: 'ROD, END-ON', label: 'ROD, END-ON' },
  { value: 'DISC, FACE-ON', label: 'DISC, FACE-ON' },
  { value: 'CUBE, FACE-ON', label: 'CUBE, FACE-ON' },
  { value: 'CUBE, EDGE-ON', label: 'CUBE, EDGE-ON' },
  { value: 'LONG RECTANGULAR MEMBER, FACE-ON', label: 'LONG RECTANGULAR MEMBER, FACE-ON' },
  { value: 'LONG RECTANGULAR MEMBER, EDGE-ON', label: 'LONG RECTANGULAR MEMBER, EDGE-ON' },
  { value: 'NARROW STRIP, FACE-ON', label: 'NARROW STRIP, FACE-ON' },
  { value: 'User Defined', label: 'User Defined' }
] as const satisfies readonly DropdownOption[];

export const LOCATION_OPTIONS = [
  { value: 'in the air', label: 'in the air' },
  { value: 'on the surface', label: 'on the surface' }
] as const satisfies readonly DropdownOption[];

export const YES_NO_OPTIONS = [
  { value: 'No', label: 'No' },
  { value: 'Yes', label: 'Yes' }
] as const satisfies readonly DropdownOption[];

export const BOUNDARY_OPTIONS = [
  { value: 'Cantilever', label: 'Cantilever' },
  { value: 'Clamped', label: 'Clamped' }
] as const satisfies readonly DropdownOption[];

export const MATERIAL_OPTIONS = [
  { value: 'ASTM A36', label: 'ASTM A36' },
  { value: 'ASTM A441', label: 'ASTM A441' },
  { value: 'ASTM A514 Grade F', label: 'ASTM A514 Grade F' },
  { value: 'User Defined', label: 'User Defined' }
] as const satisfies readonly DropdownOption[];

export type ChargeShape = (typeof CHARGE_SHAPE_OPTIONS)[number]['value'];
export type FragmentShape = (typeof FRAGMENT_SHAPE_OPTIONS)[number]['value'];
export type LocationType = (typeof LOCATION_OPTIONS)[number]['value'];
export type YesNo = (typeof YES_NO_OPTIONS)[number]['value'];
export type BoundaryType = (typeof BOUNDARY_OPTIONS)[number]['value'];
export type MaterialType = (typeof MATERIAL_OPTIONS)[number]['value'];

export interface APIPayload {
  iCharge: number;
  W: number;
  R: number;
  le: number;
  iCD: number;
  Rt: number;
  Lt: number;
  tt: number;
  Wt: number;
  CD: number;
  rho0: number;
  po: number;
  a0: number;
  h0: number;
  K: number;
  iconstrained: boolean;
  iboundary: number;
  toughness: number;
  rhoms: number;
  ioverride: boolean;
  Ioverride: number;
  Poverride: number;
  iHemi: number;
}

export interface APIResponse {
  branch_label: string;
  iopt: number;
  rr: number;
  Re: number;
  Reff: number;
  AD: number;
  Xx: number;
  H: number;
  b: number;
  L: number;
  Ac: number;
  M: number;
  betta: number;
  I00: number;
  It: number;
  y: number;
  x1: number;
  y1: number;
  z1: number;
  x3: number;
  y3: number;
  Lp: number;
  a1: number;
  v0: number;
  v0_tp13: number;
  x4: number;
  y4: number;
  R4: number;
  warning_flag: boolean;
  strike_velocities: number[];
  text_panels: {
    primary: string[];
    launch_velocity: string[];
    tp13_range: string[];
  };
  charts: Record<string, { mime: string; base64: string }>;
}

type FragmentShapeMeta = {
  index: number;
  rtLabel: string;
  showLt: boolean;
  showThickness: boolean;
  defaultCd: number | null;
};

const DEFAULT_DRAG_COEFFICIENTS: Array<number | null> = [1.2, 0.47, 0.82, 1.17, 1.05, 0.8, 2.05, 1.55, 1.98, null];

const FRAGMENT_META_BY_SHAPE: Record<FragmentShape, FragmentShapeMeta> = {
  'CIRCULAR CYLINDER (LONG ROD), SIDE-ON': { index: 1, rtLabel: 'Rt', showLt: true, showThickness: false, defaultCd: DEFAULT_DRAG_COEFFICIENTS[0] },
  SPHERE: { index: 2, rtLabel: 'Rt', showLt: false, showThickness: false, defaultCd: DEFAULT_DRAG_COEFFICIENTS[1] },
  'ROD, END-ON': { index: 3, rtLabel: 'Rt', showLt: true, showThickness: false, defaultCd: DEFAULT_DRAG_COEFFICIENTS[2] },
  'DISC, FACE-ON': { index: 4, rtLabel: 'Rt or b/2', showLt: false, showThickness: true, defaultCd: DEFAULT_DRAG_COEFFICIENTS[3] },
  'CUBE, FACE-ON': { index: 5, rtLabel: 'h/2', showLt: false, showThickness: false, defaultCd: DEFAULT_DRAG_COEFFICIENTS[4] },
  'CUBE, EDGE-ON': { index: 6, rtLabel: 'Rt', showLt: false, showThickness: false, defaultCd: DEFAULT_DRAG_COEFFICIENTS[5] },
  'LONG RECTANGULAR MEMBER, FACE-ON': { index: 7, rtLabel: 'b/2', showLt: true, showThickness: true, defaultCd: DEFAULT_DRAG_COEFFICIENTS[6] },
  'LONG RECTANGULAR MEMBER, EDGE-ON': { index: 8, rtLabel: 'b/2', showLt: true, showThickness: false, defaultCd: DEFAULT_DRAG_COEFFICIENTS[7] },
  'NARROW STRIP, FACE-ON': { index: 9, rtLabel: 'b/2', showLt: true, showThickness: true, defaultCd: DEFAULT_DRAG_COEFFICIENTS[8] },
  'User Defined': { index: 10, rtLabel: 'b/2', showLt: true, showThickness: false, defaultCd: DEFAULT_DRAG_COEFFICIENTS[9] }
};

export const FRAGMENT_PREVIEW_BY_SHAPE: Partial<Record<FragmentShape, string>> = {
  'CIRCULAR CYLINDER (LONG ROD), SIDE-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t281.png`,
  SPHERE: `${DJANGO_STATIC_PREVIEW_ROOT}/t282.png`,
  'ROD, END-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t283.png`,
  'DISC, FACE-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t284.png`,
  'CUBE, FACE-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t285.png`,
  'CUBE, EDGE-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t286.png`,
  'LONG RECTANGULAR MEMBER, FACE-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t287.png`,
  'LONG RECTANGULAR MEMBER, EDGE-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t288.png`,
  'NARROW STRIP, FACE-ON': `${DJANGO_STATIC_PREVIEW_ROOT}/t289.png`
};

const MATERIAL_TOUGHNESS: Record<Exclude<MaterialType, 'User Defined'>, number> = {
  'ASTM A36': 12000,
  'ASTM A441': 15000,
  'ASTM A514 Grade F': 19000
};

const GRAVITY = 32.174 * 12;

export function getFragmentShapeMeta(shape: FragmentShape): FragmentShapeMeta {
  return FRAGMENT_META_BY_SHAPE[shape];
}

export function getChargeShapeIndex(shape: ChargeShape): number {
  return shape === 'Spherical' ? 1 : 2;
}

export function getBoundaryIndex(boundary: BoundaryType): number {
  return boundary === 'Cantilever' ? 1 : 2;
}

export function getLocationFactor(location: LocationType): number {
  return location === 'in the air' ? 2 : 4;
}

export function getMaterialPresetToughness(material: MaterialType): number | null {
  return material === 'User Defined' ? null : MATERIAL_TOUGHNESS[material];
}

function getFragmentGeometry(iCD: number, Rt: number, Lt: number, t: number) {
  let thickness = t;
  if (thickness <= Number.EPSILON) {
    if (iCD === 4) thickness = 1;
    if (iCD === 7 || iCD === 8) thickness = 2 * Rt;
    if (iCD === 9) thickness = 1;
  }

  if (iCD === 1) return { L: Lt, Ac: Math.PI * Rt ** 2 };
  if (iCD === 2) return { L: 2 * Rt, Ac: Math.PI * Rt ** 2 };
  if (iCD === 3) return { L: Lt, Ac: Math.PI * Rt ** 2 };
  if (iCD === 4) return { L: 2 * Rt, Ac: 2 * Math.PI * Rt * thickness };
  if (iCD === 5) return { L: 2 * Rt, Ac: (2 * Rt) ** 2 };
  if (iCD === 6) return { L: 2 * Rt, Ac: (2 * Rt) ** 2 };
  if (iCD === 7) return { L: Lt, Ac: 2 * Rt * thickness };
  if (iCD === 8) return { L: Lt, Ac: 2 * Rt * thickness };
  if (iCD === 9) return { L: Lt, Ac: 2 * Rt * thickness };
  return { L: Lt, Ac: Math.PI * Rt ** 2 };
}

// Mirrors the MATLAB update_rhoms callback so the constrained-only rho_f field
// reflects the same geometry assumptions before the backend request is sent.
export function computeRhoms(shape: FragmentShape, Rt: number, Lt: number, tt: number, Wt: number): number {
  const meta = getFragmentShapeMeta(shape);
  const { L, Ac } = getFragmentGeometry(meta.index, Rt, Lt, tt);
  const M = (Wt / GRAVITY) * 1e6;
  return M / L / Ac;
}

const formatNumber = (value: number, digits = 3) => value.toFixed(digits);

export const RESPONSE_DISPLAY_CONFIG: SectionConfig<APIResponse>[] = [
  {
    title: 'Branch Selection',
    fields: [
      { label: 'Branch', getValue: data => data.branch_label },
      { label: 'Warning', getValue: data => (data.warning_flag ? 'Yes' : 'No') },
      { label: 'iopt', getValue: data => data.iopt },
      { label: 'rr', getValue: data => formatNumber(data.rr, 4) },
      { label: 'Re', getValue: data => formatNumber(data.Re, 4) },
      { label: 'Reff', getValue: data => formatNumber(data.Reff, 4) }
    ]
  },
  {
    title: 'Fragment Geometry',
    fields: [
      { label: 'AD', getValue: data => formatNumber(data.AD) },
      { label: 'Xx', getValue: data => formatNumber(data.Xx) },
      { label: 'H', getValue: data => formatNumber(data.H) },
      { label: 'b', getValue: data => formatNumber(data.b) },
      { label: 'L', getValue: data => formatNumber(data.L) },
      { label: 'Ac', getValue: data => formatNumber(data.Ac) },
      { label: 'M', getValue: data => formatNumber(data.M) },
      { label: 'betta', getValue: data => formatNumber(data.betta) }
    ]
  },
  {
    title: 'Launch and Range',
    fields: [
      { label: 'I00', getValue: data => formatNumber(data.I00) },
      { label: 'It', getValue: data => formatNumber(data.It) },
      { label: 'v0', getValue: data => `${formatNumber(data.v0)} ft/sec` },
      { label: 'v0_tp13', getValue: data => formatNumber(data.v0_tp13) },
      { label: 'x4', getValue: data => formatNumber(data.x4) },
      { label: 'y4', getValue: data => formatNumber(data.y4) },
      { label: 'R4', getValue: data => `${formatNumber(data.R4)} ft` },
      { label: 'x1', getValue: data => formatNumber(data.x1), show: data => data.x1 >= 0 },
      { label: 'y1', getValue: data => formatNumber(data.y1), show: data => data.y1 >= 0 },
      { label: 'z1', getValue: data => formatNumber(data.z1), show: data => data.z1 >= 0 },
      { label: 'x3', getValue: data => formatNumber(data.x3), show: data => data.x3 >= 0 },
      { label: 'y3', getValue: data => formatNumber(data.y3), show: data => data.y3 >= 0 },
      { label: 'Lp', getValue: data => formatNumber(data.Lp), show: data => data.Lp >= 0 },
      { label: 'a1', getValue: data => formatNumber(data.a1), show: data => data.a1 >= 0 }
    ]
  },
  {
    title: 'Strike Velocity',
    fields: [
      { label: 'Count', getValue: data => data.strike_velocities.length },
      {
        label: 'Min',
        getValue: data => (data.strike_velocities.length ? formatNumber(Math.min(...data.strike_velocities)) : 'N/A')
      },
      {
        label: 'Max',
        getValue: data => (data.strike_velocities.length ? formatNumber(Math.max(...data.strike_velocities)) : 'N/A')
      },
      {
        label: 'Sample',
        getValue: data =>
          data.strike_velocities
            .slice(0, 3)
            .map(value => formatNumber(value))
            .join(', ')
      }
    ]
  }
];

export const TEXT_PANEL_TITLES: Record<keyof APIResponse['text_panels'], string> = {
  primary: 'MATLAB Summary',
  launch_velocity: 'Launch Velocity Estimate',
  tp13_range: 'TP-13 / Range Estimate'
};
